/**
 * Rule Expression Builder for Workflow Rules.
 *
 * Provides a visual editor for the `expression` JSONB column on workflow rules.
 * The expression JSON shape: { expression, tolerance?, aggregation? }
 * See docs/architecture/rule-writing-guide.md for the full spec.
 */
import { useCallback, useMemo, useState } from "react";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";

// =============================================================================
// TYPES
// =============================================================================

interface Aggregation {
    function: string;
    childType: string;
    field: string;
    as: string;
}

interface RuleExpression {
    expression?: string;
    tolerance?: number;
    aggregation?: Aggregation;
}

interface RuleExpressionBuilderProps {
    /** The current JSON string of the expression */
    value: string;
    /** Called with the updated JSON string */
    onChange: (json: string) => void;
    /** The rule type selected (e.g. "Required", "Range", "Comparison") — used for contextual hints */
    ruleType?: string;
    isDisabled?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const AGGREGATION_FUNCTIONS: SelectItemType[] = [
    { id: "SUM", label: "SUM" },
    { id: "COUNT", label: "COUNT" },
    { id: "AVG", label: "AVG" },
    { id: "MIN", label: "MIN" },
    { id: "MAX", label: "MAX" },
];

/** Expression hints per rule type */
const EXPRESSION_HINTS: Record<string, string> = {
    Required: "!IsEmpty(field_name)",
    Format: "Matches(field_name, '^\\\\d{4}-\\\\d{2}-\\\\d{2}$')",
    Range: "field_name >= 0 && field_name <= 1000",
    ToleranceAbsolute: "Abs(field_a - field_b) <= tolerance",
    TolerancePercent: "Abs(field_a - field_b) / field_b <= tolerance",
    Duplicate: "!IsDuplicate(vendor_id, invoice_number)",
    ValidValues: "InList(field_name, 'A', 'B', 'C')",
    Pattern: "Matches(field_name, '^PO-\\\\d{6}$')",
    ConditionalRequired: "IsEmpty(condition_field) || !IsEmpty(required_field)",
    Comparison: "due_date >= invoice_date",
    LineSum: "Abs(header_total - lines_sum) < 0.01",
};

// =============================================================================
// PARSE / SERIALIZE
// =============================================================================

function parseExpression(json: string): RuleExpression | null {
    if (!json.trim()) return { expression: "" };
    try {
        const parsed = JSON.parse(json);
        if (typeof parsed !== "object" || Array.isArray(parsed)) return null;
        return parsed as RuleExpression;
    } catch {
        return null;
    }
}

function serializeExpression(expr: RuleExpression): string {
    // Clean up: remove empty optional fields
    const clean: Record<string, unknown> = {};
    if (expr.expression) clean.expression = expr.expression;
    if (expr.tolerance != null && expr.tolerance !== 0) clean.tolerance = expr.tolerance;
    if (expr.aggregation && expr.aggregation.function && expr.aggregation.childType && expr.aggregation.field && expr.aggregation.as) {
        clean.aggregation = expr.aggregation;
    }
    if (Object.keys(clean).length === 0) return "";
    return JSON.stringify(clean, null, 2);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function RuleExpressionBuilder({ value, onChange, ruleType, isDisabled }: RuleExpressionBuilderProps) {
    const [rawMode, setRawMode] = useState(false);

    const parsed = useMemo(() => parseExpression(value), [value]);
    const isMalformed = parsed === null;
    const effectiveRawMode = rawMode || isMalformed;

    const [showAggregation, setShowAggregation] = useState(() => {
        const p = parseExpression(value);
        return p?.aggregation != null;
    });

    const updateExpression = useCallback(
        (updates: Partial<RuleExpression>) => {
            const current = parsed ?? { expression: "" };
            const next = { ...current, ...updates };
            onChange(serializeExpression(next));
        },
        [parsed, onChange],
    );

    const updateAggregation = useCallback(
        (updates: Partial<Aggregation>) => {
            const current = parsed ?? { expression: "" };
            const agg = { ...(current.aggregation ?? { function: "SUM", childType: "", field: "", as: "" }), ...updates };
            updateExpression({ aggregation: agg });
        },
        [parsed, updateExpression],
    );

    const hint = ruleType ? (EXPRESSION_HINTS[ruleType] ?? "") : "";

    return (
        <div className="space-y-3">
            {/* Header with raw toggle */}
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-secondary">Expression</label>
                <button
                    type="button"
                    className="cursor-pointer text-xs font-medium text-brand-tertiary transition-colors hover:text-brand-primary"
                    onClick={() => setRawMode(!rawMode)}
                >
                    {effectiveRawMode ? "Visual Editor" : "Raw JSON"}
                </button>
            </div>

            {isMalformed && !rawMode && <p className="text-xs text-warning-primary">Invalid JSON — switch to Raw JSON to fix.</p>}

            {effectiveRawMode ? (
                <TextArea
                    value={value}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    placeholder='{"expression": "amount > 0"}'
                    rows={6}
                    textAreaClassName="font-mono text-xs"
                />
            ) : (
                <div className="space-y-3 rounded-lg border border-secondary p-3">
                    {/* Expression input */}
                    <div>
                        <Input
                            label="NCalc Expression"
                            value={parsed?.expression ?? ""}
                            onChange={(v) => updateExpression({ expression: v })}
                            size="sm"
                            isDisabled={isDisabled}
                            placeholder={hint || "e.g. amount > 0 && !IsEmpty(invoice_number)"}
                        />
                        {hint && (
                            <p className="mt-1 text-xs text-quaternary">
                                Hint for {ruleType}: <code className="rounded bg-secondary_subtle px-1 font-mono text-[10px]">{hint}</code>
                            </p>
                        )}
                    </div>

                    {/* Tolerance */}
                    <Input
                        label="Tolerance (optional)"
                        value={parsed?.tolerance != null ? String(parsed.tolerance) : ""}
                        onChange={(v) => updateExpression({ tolerance: v ? Number(v) : undefined })}
                        size="sm"
                        type="number"
                        isDisabled={isDisabled}
                        placeholder="e.g. 0.01"
                    />

                    {/* Aggregation toggle */}
                    <Toggle
                        label="Include aggregation (child document calculation)"
                        isSelected={showAggregation}
                        onChange={(v) => {
                            setShowAggregation(v);
                            if (!v) {
                                // Remove aggregation
                                const current = parsed ?? { expression: "" };
                                const { aggregation: _, ...rest } = current;
                                onChange(serializeExpression(rest));
                            }
                        }}
                        size="sm"
                    />

                    {/* Aggregation fields */}
                    {showAggregation && (
                        <div className="space-y-3 rounded-lg border border-dashed border-secondary bg-secondary_subtle p-3">
                            <p className="text-xs font-medium text-secondary">Aggregation — Calculate a value from child documents</p>
                            <div className="grid grid-cols-2 gap-3">
                                <Select
                                    label="Function"
                                    items={AGGREGATION_FUNCTIONS}
                                    selectedKey={parsed?.aggregation?.function ?? "SUM"}
                                    onSelectionChange={(key) => updateAggregation({ function: String(key) })}
                                    size="sm"
                                    isDisabled={isDisabled}
                                >
                                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                </Select>
                                <Input
                                    label="Child Type (document type code)"
                                    value={parsed?.aggregation?.childType ?? ""}
                                    onChange={(v) => updateAggregation({ childType: v })}
                                    size="sm"
                                    isDisabled={isDisabled}
                                    placeholder="e.g. invoice_line"
                                />
                                <Input
                                    label="Field (field code to aggregate)"
                                    value={parsed?.aggregation?.field ?? ""}
                                    onChange={(v) => updateAggregation({ field: v })}
                                    size="sm"
                                    isDisabled={isDisabled}
                                    placeholder="e.g. line_total"
                                />
                                <Input
                                    label="As (variable name in expression)"
                                    value={parsed?.aggregation?.as ?? ""}
                                    onChange={(v) => updateAggregation({ as: v })}
                                    size="sm"
                                    isDisabled={isDisabled}
                                    placeholder="e.g. lines_sum"
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
